import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { forgotPasswordResetSuperAdmin } from '../../service/endpoints';
import '../../Auth.css';

const OTP_LENGTH = 6;

const ForgotPasswordOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      toast.error('Session expired. Please enter your email again.');
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleOtpChange = (i, value) => {
    if (!value) {
      const next = [...otp];
      next[i] = '';
      setOtp(next);
      return;
    }

    // Support multi-digit paste/auto-fill into a single OTP input.
    const digits = value.replace(/\D/g, '');
    if (!digits) return;

    const next = [...otp];
    for (let idx = 0; idx < digits.length && i + idx < OTP_LENGTH; idx++) {
      next[i + idx] = digits[idx];
    }
    setOtp(next);

    const nextFocus = Math.min(i + digits.length, OTP_LENGTH - 1);
    document.getElementById(`otp-${nextFocus}`)?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) document.getElementById(`otp-${i - 1}`)?.focus();
  };

  const handleOtpPaste = (i, e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const digits = (pasted || '').replace(/\D/g, '');
    if (!digits) return;

    const next = [...otp];
    for (let idx = 0; idx < digits.length && i + idx < OTP_LENGTH; idx++) {
      next[i + idx] = digits[idx];
    }
    setOtp(next);

    const nextFocus = Math.min(i + digits.length, OTP_LENGTH - 1);
    document.getElementById(`otp-${nextFocus}`)?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length !== OTP_LENGTH) {
      toast.error('Please enter the 6-digit OTP.');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPasswordResetSuperAdmin({
        email,
        otp: otpStr,
        newPassword: newPassword.trim(),
        confirmPassword: confirmPassword.trim(),
      });
      if (res?.success) {
        toast.success(res?.message ?? res?.data?.message ?? 'Password reset successfully.');
        setTimeout(() => navigate('/login'), 800);
      } else {
        toast.error(res?.message ?? res?.error?.message ?? res?.data?.error_msg ?? 'Failed to reset password.');
        setLoading(false);
      }
    } catch (err) {
      const msg = err?.error_msg ?? err?.message ?? 'Something went wrong. Please try again.';
      toast.error(msg);
      setLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="container-fluid auth-page h-100 d-flex align-items-center justify-content-center">
      <div className="Login-card auth-card">
        <div className="card-body">
          <h3 className="card-title mb-0"><span className="span"></span>Reset Password</h3>
          <p className="auth-subtitle">OTP sent to <strong>{email}</strong></p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="mb-3">
              <label className="form-label auth-label">OTP (6 digits)</label>
              <div className="d-flex gap-1 justify-content-center">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    id={`otp-${i}`}
                    className="form-control text-center auth-otp-input"
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={(e) => handleOtpPaste(i, e)}
                  />
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label auth-label">New password</label>
              <div className="input-group auth-input-group">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="form-control auth-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-group-text auth-eye-toggle"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  {showNewPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.359 11.238 15.147 13.026l-.708.708-2.02-2.02A8.783 8.783 0 0 1 8 13C3 13 1 8 1 8a13.133 13.133 0 0 1 3.244-4.156L.146 1.746l.708-.708 14 14-.708.708-1.787-1.787zM5.172 4.464A5.9 5.9 0 0 0 2.22 8c.5.95 2.245 3.75 5.78 3.75 1.015 0 1.906-.23 2.665-.59l-1.124-1.124A2.5 2.5 0 0 1 5.965 6.46L5.172 5.667z"/>
                      <path d="M10.477 8.649 7.35 5.523a2 2 0 0 1 3.126 3.126zM8 3c5 0 7 5 7 5a13.133 13.133 0 0 1-2.004 2.82l-.743-.743A11.91 11.91 0 0 0 13.78 8c-.5-.95-2.245-3.75-5.78-3.75-.882 0-1.675.14-2.377.358l-.748-.748A8.783 8.783 0 0 1 8 3z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.12 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.133 13.133 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label auth-label">Confirm password</label>
              <div className="input-group auth-input-group">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control auth-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Same as above"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-group-text auth-eye-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.359 11.238 15.147 13.026l-.708.708-2.02-2.02A8.783 8.783 0 0 1 8 13C3 13 1 8 1 8a13.133 13.133 0 0 1 3.244-4.156L.146 1.746l.708-.708 14 14-.708.708-1.787-1.787zM5.172 4.464A5.9 5.9 0 0 0 2.22 8c.5.95 2.245 3.75 5.78 3.75 1.015 0 1.906-.23 2.665-.59l-1.124-1.124A2.5 2.5 0 0 1 5.965 6.46L5.172 5.667z"/>
                      <path d="M10.477 8.649 7.35 5.523a2 2 0 0 1 3.126 3.126zM8 3c5 0 7 5 7 5a13.133 13.133 0 0 1-2.004 2.82l-.743-.743A11.91 11.91 0 0 0 13.78 8c-.5-.95-2.245-3.75-5.78-3.75-.882 0-1.675.14-2.377.358l-.748-.748A8.783 8.783 0 0 1 8 3z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.12 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.133 13.133 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <button type="submit" className="button auth-btn py-2 w-100" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>
          </form>

          <p className="text-center small mt-3 mb-0 auth-footer-link">
            <button type="button" onClick={() => navigate('/forgot-password')} className="text-secondary btn btn-link p-0 me-2">← Back</button>
            <button type="button" onClick={() => navigate('/login')} className="text-secondary btn btn-link p-0">Login</button>
          </p>
        </div>
      </div>
      <ToastContainer limit={2} position="top-center" />
    </div>
  );
};

export default ForgotPasswordOTP;
